import { TextAttributes } from '@opentui/core'
import React, { useEffect, useState } from 'react'

import { useTheme } from '../hooks/use-theme'

/**
 * A terminal-native take on the `thinking-orbs` aesthetic: a small dotted
 * "thought orb" built from Unicode particles, animated in place.
 *
 * The real `thinking-orbs` package renders a web `<canvas>` and cannot paint
 * inside the OpenTUI terminal renderer (no DOM), so this component recreates
 * the same visual language — sparse orbiting dots on a transparent field —
 * with text cells. Each frame advances a few dots around a 2×2 mini-orb, and
 * the dot count/look changes with the state, mirroring the npm package's
 * nine-verb idea:
 *
 *  - waiting:    two dots, slow orbit (the model is thinking)
 *  - working:    three dots, steady orbit (it is doing the work)
 *  - searching:  one sweeping dot (scanning)
 *  - connecting: dots blink in (wiring up)
 *  - retrying:   a single pulsing dot (hiccup)
 *
 * With `variant="spark"` it swaps to a rotating spark glyph — ✻ → ✺ → ✹ → ✸ —
 * the same "worked for 39s" rotating marker used by Command Code's loading
 * states, rendered as a single crisp cell.
 */

type OrbState =
  | 'waiting'
  | 'working'
  | 'searching'
  | 'connecting'
  | 'retrying'

const ORB_FRAMES: Record<string, string[]> = {
  // Braille 2×4 dot cells read as a tiny orbiting particle field.
  waiting: ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧'],
  working: ['⠁', '⠉', '⠙', '⠸', '⠼', '⠴', '⠦', '⠇'],
  searching: ['⠂', '⠄', '⡀', '⢀', '⠠', '⠐', '⠈'],
  connecting: ['⡀', '⠄', '⠂', '⠁', '⠉', '⠘', '⠨', '⢀'],
  retrying: ['⠄', '⡆', '⣶', '⣿', '⣶', '⡆', '⠄', '⠂'],
}

// Rotating spark glyphs — the Command Code-style rotating marker.
const SPARK_FRAMES = ['✻', '✺', '✹', '✸', '✹', '✺']

const ORB_INTERVAL_MS: Record<OrbState, number> = {
  waiting: 140,
  working: 110,
  searching: 130,
  connecting: 150,
  retrying: 180,
}

interface TerminalThinkingOrbProps {
  /** Which animation to show. @default 'working' */
  state?: OrbState
  /** Spark (✻ rotation) or dotted braille particle field. @default 'dots' */
  variant?: 'dots' | 'spark'
  /** Animation speed multiplier on top of the state's baked speed. @default 1 */
  speed?: number
  /** Color override; defaults to theme.primary (violet). */
  color?: string
}

export const TerminalThinkingOrb = ({
  state = 'working',
  variant = 'dots',
  speed = 1,
  color,
}: TerminalThinkingOrbProps) => {
  const theme = useTheme()
  const [frame, setFrame] = useState(0)

  const frames =
    variant === 'spark' ? SPARK_FRAMES : (ORB_FRAMES[state] ?? ORB_FRAMES.working)
  const interval = Math.max(
    40,
    (variant === 'spark' ? 140 : ORB_INTERVAL_MS[state] ?? 110) / speed,
  )

  useEffect(() => {
    const id = setInterval(() => {
      setFrame((prev) => (prev + 1) % frames.length)
    }, interval)
    return () => clearInterval(id)
  }, [interval, frames.length])

  return (
    <span fg={color ?? theme.primary} attributes={TextAttributes.BOLD}>
      {frames[frame]}
    </span>
  )
}

